// src/components/MyTasks.jsx
// User-facing task list — assigned, accepted, and completed tasks.
// Sections: Action required · In progress · Completed.

import React, { useState as useState_MT } from "react";
import * as PPIcons from "./_icons";
import { Avatar, Button, Chip, Empty, SeverityChip, StatusChip, Tabs, formatRelative } from "./_primitives";

function MyTasks({
  tasks = [],
  currentUid,
  allPosts = [],
  completeTask, acceptTask, denyTask, acceptSecondaryTask, denySecondaryTask,
  showToast,
}) {
  const [tab, setTab] = useState_MT("active");

  const mine = tasks.filter(t =>
    t.assignedTo === currentUid || t.assignedTo2 === currentUid
  );
  const pending = mine.filter(t => t.accepted === false || t.accepted == null);
  const active = mine.filter(t => t.status === "open" && t.accepted === true);
  const done   = mine.filter(t => t.status === "completed");

  const list = tab === "pending" ? pending : tab === "completed" ? done : active;

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "24px 24px 60px", width: "100%" }}>
      <header style={{ marginBottom: 18 }}>
        <h1 style={{
          fontSize: 22, fontWeight: 700, color: "var(--text-strong)",
          letterSpacing: "-0.01em",
        }}>My tasks</h1>
        <div style={{ fontSize: 12, color: "var(--text-faint)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
          {active.length} active · {pending.length} pending · {done.length} completed
        </div>
      </header>

      <Tabs
        value={tab} onChange={setTab}
        tabs={[
          { id: "active",    label: "Active",    count: active.length },
          { id: "pending",   label: "Pending",   count: pending.length },
          { id: "completed", label: "Completed", count: done.length },
        ]}
      />

      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        {list.length === 0 ? (
          <Empty
            icon={<PPIcons.IconCheck />}
            title={
              tab === "active" ? "No active tasks. Nice."
              : tab === "pending" ? "Nothing waiting for you."
              : "No completed tasks yet."
            }
            sub="Tasks assigned to you appear here."
          />
        ) : (
          list.map(t => (
            <TaskCard
              key={t.id} task={t} posts={allPosts}
              onComplete={() => completeTask && completeTask(t.id, t.postIds || [])}
              onAccept={() => acceptTask && acceptTask(t.id)}
              onDeny={() => denyTask && denyTask(t.id)}
              showActions={tab === "pending"}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, posts = [], onComplete, onAccept, onDeny, showActions }) {
  const linkedPosts = (task.postIds || []).map(id => posts.find(p => p.id === id)).filter(Boolean);
  const [expanded, setExpanded] = useState_MT(false);
  const isCompleted = task.status === "completed";

  return (
    <article className="pp-surface" style={{ overflow: "hidden" }}>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              {task.autoGenerated && (
                <Chip tone="ai" dot={false}>
                  <PPIcons.IconSparkle style={{ width: 11, height: 11 }} /> Auto-suggested
                </Chip>
              )}
              {isCompleted ? <StatusChip status="resolved" /> : <StatusChip status="open" />}
              {task.severity && <SeverityChip severity={task.severity} />}
            </div>
            <h3 style={{
              fontSize: 15, fontWeight: 700, color: "var(--text-strong)",
              letterSpacing: "-0.005em",
              textDecoration: isCompleted ? "line-through" : "none",
              textDecorationColor: "var(--text-faint)",
            }}>{task.title}</h3>
            <p style={{
              fontSize: 13, color: "var(--text-dim)", lineHeight: 1.5, marginTop: 4,
            }}>{task.description}</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Avatar name={task.assignedToName} size="sm" brand />
            <Button variant="ghost" size="sm" icon={<PPIcons.IconMore />} />
          </div>
        </div>

        <div style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          fontSize: 12, color: "var(--text-faint)", fontFamily: "var(--font-mono)",
        }}>
          <span>{formatRelative(task.createdAt)}</span>
          {linkedPosts.length > 0 && (
            <button
              onClick={() => setExpanded(e => !e)}
              style={{
                background: "transparent", border: 0, padding: 0,
                color: "var(--link)", cursor: "pointer", fontFamily: "inherit",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}>
              <PPIcons.IconLink style={{ width: 10, height: 10 }} />
              {linkedPosts.length} linked post{linkedPosts.length === 1 ? "" : "s"}
              <PPIcons.IconChevD style={{
                width: 10, height: 10,
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }} />
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, paddingTop: 4 }}>
          {showActions ? (
            <>
              <Button variant="primary" size="sm" icon={<PPIcons.IconCheck />} onClick={onAccept}>
                Accept
              </Button>
              <Button variant="ghost" size="sm" onClick={onDeny}>Deny</Button>
            </>
          ) : !isCompleted && (
            <>
              <Button variant="primary" size="sm" icon={<PPIcons.IconCheckCircle />} onClick={onComplete}>
                Mark complete
              </Button>
              <Button variant="ghost" size="sm">Add note</Button>
            </>
          )}
        </div>
      </div>

      {expanded && linkedPosts.length > 0 && (
        <div style={{
          background: "var(--surface-2)", padding: "12px 16px",
          borderTop: "1px solid var(--divider)",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ fontSize: 11, color: "var(--text-faint)", fontFamily: "var(--font-mono)", textTransform: "uppercase", letterSpacing: 0.06 }}>
            Linked pinch points
          </div>
          {linkedPosts.map(p => (
            <div key={p.id} style={{
              padding: "8px 10px", background: "var(--surface)",
              border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <SeverityChip severity={p.severity} />
              <span style={{
                fontSize: 12, color: "var(--text)", flex: 1,
                display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden",
              }}>{p.description}</span>
              <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--text-faint)" }}>
                ↑ {p.upvotes || 0}
              </span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

export default MyTasks;
